// Uma linha da EAP na tela de Desembolso: identidade do nó + a distribuição mensal do
// desembolso Ampli no período selecionado + a ação de editar a regra daquele nó.
//
// Mesma moldura visual das linhas da Projeção Financeira (`projectionTable/TableItem`):
// as classes `item`/`accordion-item-body`/`accordion-sticky-header`/`month-divider`/
// `table-date`/`accordion-content` são o padrão de layout da Ampli para esta grade, e o
// recuo e o cinza por nível seguem os mesmos valores — o cliente navega as duas telas
// como se fossem a mesma tabela.
//
// A diferença deliberada: UMA métrica por mês (o desembolso), não as dez colunas
// configuráveis da projeção. Esta tela existe para cadastrar a forma de pagar, então a
// grade mostra o que a regra produz e nada mais.
import { useState } from "react";

import { useProjectContext } from "@contexts/projects";
import { formatNumberToLocale } from "@utils/formatNumber";

import { ruleKeys } from "../engine";
import { labelOfCompetence } from "../months";
import { describeProfile } from "../profile";
import DistributionForm from "../DistributionForm";
import { resolveDistribution } from "../budgetPlanner";
import SliceAdjustForm from "../SliceAdjustForm";
import { contributionOf, monthlyAvailability } from "../slice";
import { useEapTable } from "./context";

const LEVEL_BACKGROUND = Object.freeze({
  unit: "#f8f8f8",
  cell: "#f0f0f0",
  step: "#e8e8e8",
  substep: "#e0e0e0",
  service: "#d8d8d8",
});

const LEVEL_INDENT = Object.freeze({
  unit: "0px",
  cell: "8px",
  step: "16px",
  substep: "24px",
  service: "32px",
});

/** `[{ month, year, value }]` → valor do mês `"MM/AAAA"`, ou `null` se o nó não tem. */
const monthlyValue = (item, date) => {
  const series = item?.monthlyProjection?.ampliDisbursement;
  if (!Array.isArray(series) || series.length === 0) return null;
  const found = series.find(
    (point) => `${String(point.month).padStart(2, "0")}/${point.year}` === date,
  );
  return found ? found.value : null;
};

const EapRuleRow = ({
  item,
  type,
  typeTitle,
  months,
  ChildAccordionComponent,
}) => {
  const [open, setOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const {
    readOnly, rulesByNodeKey, onEditRule, selectedMonths, onSliceAdjust, followsLabel,
    distributionEditor,
  } = useEapTable();
  // Contexto lido UMA vez no corpo do componente: `checkIfCubBased`/
  // `formatNumberToCurrency` leem contexto por dentro (hooks disfarçados), e chamá-los
  // uma vez por mês faria a contagem de hooks depender do tamanho do período.
  const { cubBased, activeProject } = useProjectContext();
  const indexerValue = activeProject?.originalIndexer?.value;

  const formatValue = (value) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return "—";
    if (!cubBased) return formatNumberToLocale(value);
    const basis = indexerValue && indexerValue !== 0 ? indexerValue : null;
    return basis === null ? "—" : formatNumberToLocale(value / basis);
  };

  const wbsCodeId = item?.wbs_code_id;
  const nodeKey = wbsCodeId ? ruleKeys.node(wbsCodeId) : null;
  const rule = nodeKey ? rulesByNodeKey[nodeKey] ?? null : null;
  const label = item?.description ?? item?.building_unit_name ?? wbsCodeId;

  // O recorte por meses (planning-workspace): sem seleção a linha é a de sempre; com ela, só
  // as linhas com valor nos meses escolhidos aparecem — e mostram a soma desses meses.
  const sliceActive = selectedMonths.size > 0;
  const monthlyPoints = item?.monthlyProjection?.ampliDisbursement;
  const sliceCents = sliceActive
    ? Math.round(contributionOf(monthlyPoints, selectedMonths) * 100)
    : null;
  if (sliceActive && !(sliceCents > 0)) return null;

  // A origem "criar do zero": cada nível mostra a distribuição que o governa (a própria, a
  // herdada de um nível acima ou nenhuma) e oferece defini-la ou sobrescrevê-la.
  const distribution = (() => {
    if (!distributionEditor || !wbsCodeId) return null;
    const root = (distributionEditor.roots ?? []).find((candidate) => String(wbsCodeId).startsWith(candidate));
    if (!root) return null;
    const governing = resolveDistribution(wbsCodeId, root, distributionEditor.distributions);
    return { governing, own: governing?.node === wbsCodeId };
  })();
  const availability = sliceActive ? monthlyAvailability(monthlyPoints, selectedMonths) : [];

  return (
    <div className="item" style={{ marginLeft: LEVEL_INDENT[type] ?? "0px" }}>
      <div
        className="accordion-item-body"
        style={{ backgroundColor: LEVEL_BACKGROUND[type], display: "block" }}
      >
        <div className="column-span-2-tablet">
          <div className="flex wrap-wrap wrap-nowrap-tablet gap-16 align-center">
            <div
              className="flex align-center gap-8 accordion-sticky-header"
              style={{ backgroundColor: LEVEL_BACKGROUND[type] }}
            >
              {ChildAccordionComponent ? (
                <button
                  type="button"
                  className={`accordion-toggle ${open ? "active" : ""}`}
                  aria-expanded={open}
                  aria-label={`${open ? "Recolher" : "Expandir"} ${label}`}
                  onClick={() => setOpen((value) => !value)}
                />
              ) : null}
              <div className="grid ampli-eap-row__identity">
                <span className="accordion-label">
                  {`EAP #${item?.building_unit_id ?? ""}${
                    item?.wbs ? `.${item.wbs}` : ""
                  } / ${typeTitle}`}
                </span>
                <span className="accordion-title" title={label}>{label}</span>
                <span className={`ampli-eap-row__state${rule ? " ampli-eap-row__state--rule" : ""}`}>
                  {rule ? describeProfile(rule.profile) : followsLabel ?? "à vista (padrão)"}
                </span>
                {distribution ? (
                  <span className="accordion-label" data-testid="distribution-state">
                    {distribution.governing
                      ? distribution.own
                        ? `Distribuição própria: início ${labelOfCompetence(distribution.governing.startMonth)}, ${distribution.governing.profile.length} ${
                          distribution.governing.profile.length === 1 ? "parcela" : "parcelas"
                        }`
                        : `Herda a distribuição de ${distribution.governing.node}`
                      : "Sem distribuição"}
                  </span>
                ) : null}
                {sliceActive ? (
                  <span className="accordion-label" data-testid="slice-value">
                    {`Meses selecionados: ${formatValue(sliceCents / 100)}`}
                  </span>
                ) : null}
              </div>
              <div className="ampli-eap-row__actions">
              {distribution && !readOnly ? (
                <button
                  type="button"
                  className="button button-outline small"
                  aria-expanded={distributing}
                  onClick={() => setDistributing((value) => !value)}
                >
                  {distribution.own
                    ? "Editar distribuição"
                    : distribution.governing
                      ? "Sobrescrever distribuição"
                      : "Definir distribuição"}
                </button>
              ) : null}
              {sliceActive && !readOnly && onSliceAdjust && nodeKey ? (
                <button
                  type="button"
                  className="button button-outline small"
                  aria-expanded={adjusting}
                  onClick={() => setAdjusting((value) => !value)}
                >
                  Ajustar desembolso
                </button>
              ) : null}
              {!readOnly && nodeKey ? (
                <button
                  type="button"
                  className="button button-outline small"
                  onClick={() =>
                    onEditRule?.(
                      {
                        kind: "node",
                        key: nodeKey,
                        level: type,
                        label,
                      },
                      rule,
                    )
                  }
                >
                  {rule ? "Editar regra" : "Definir regra"}
                </button>
              ) : null}
              </div>
            </div>
            {months.map((date) => (
              <div key={date} style={{ width: "95px", position: "relative" }}>
                <div className="month-divider" />
                <p className="table-date">{date}</p>
                <div className="accordion-content columns-1-tablet">
                  <div className="data">
                    <span className="label">Desembolso</span>
                    <span className="value">
                      {formatValue(monthlyValue(item, date))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {distribution && distributing && !readOnly ? (
        <DistributionForm
          label={label}
          initial={distribution.own ? distribution.governing : null}
          onCancel={() => setDistributing(false)}
          onSave={async (value) => {
            const result = await distributionEditor.onSave(wbsCodeId, value);
            if (result?.ok) setDistributing(false);
            return result;
          }}
          onClear={
            distribution.own
              ? async () => {
                  const result = await distributionEditor.onSave(wbsCodeId, null);
                  if (result?.ok) setDistributing(false);
                  return result;
                }
              : undefined
          }
        />
      ) : null}
      {sliceActive && adjusting && !readOnly ? (
        <SliceAdjustForm
          label={label}
          availability={availability}
          onCancel={() => setAdjusting(false)}
          onDone={() => setAdjusting(false)}
          submitLines={(lines, toCompetence, description) =>
            onSliceAdjust(wbsCodeId, lines, toCompetence, description)
          }
        />
      ) : null}
      {open && ChildAccordionComponent ? (
        <ChildAccordionComponent parent={wbsCodeId} months={months} />
      ) : null}
    </div>
  );
};

export default EapRuleRow;
